import type { ReactElement, ReactNode } from 'react';
import { useSetAtom, WritableAtom } from 'jotai';

import React, { Suspense, useMemo, useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import tw, { styled } from 'twin.macro';
import { Heading, Checkbox, Button, Spinner, Input, FormControl, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi'
import { IoMdSearch } from 'react-icons/io'
import { atom, useAtom, useAtomValue } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import * as R from 'ramda';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery } from '@tanstack/react-query';

import AppLayout from '@/providers/AppLayout';
import { trpcQuery, trpcVanilla } from '@/providers/trpc';
import ConditionallyRender from '@/components/ConditionallyRender';
import IncubationUserBar from '@/components/IncubationUserBar/IncubationUserBar';
import { transformUINft, LobbyCard } from '@/components/ShellCard/ShellCard';
import { CareerIcon } from '@/components/icons/CareerIcon';
import { SpeciesIcon } from '@/components/icons/SpeciesIcon';
import { formatCurrency } from '@/utils/formatCurrency';
import ActivityListIcon from '../lib/svgs/activity.svg'

const ITEMS_PER_PAGE = 20

//
// Atoms
//

const listQueryAtom = atomWithReset<Parameters<typeof trpcQuery.nfts.list.useQuery>[0]>({ collectionIds: [2] })

const atomWithListIncludes = (propName: string, expected: string) => {
  return atom<boolean, any>(
    get => R.includes(expected, R.propOr([], propName, get(listQueryAtom))),
    (get, set, _value) => {
      const args = get(listQueryAtom)
      const prev: string[] = R.propOr([], propName, args)
      set(listQueryAtom, { ...args, [propName]: R.includes(expected, prev) ? R.without([expected], prev) : [...prev, expected] })
    }
  )
}

const forSaleAtom = atom<boolean, any>(
  get => get(listQueryAtom).saleType === 'listed',
  (get, set, _value) => {
    const args = get(listQueryAtom)
    const prev = args.saleType
    set(listQueryAtom, { ...args, saleType: prev === 'listed' ? 'all' : 'listed' })
  }
)

const notForSaleAtom = atom<boolean, any>(
  get => get(listQueryAtom).saleType === 'unlisted',
  (get, set, _value) => {
    const args = get(listQueryAtom)
    const prev = args.saleType
    set(listQueryAtom, { ...args, saleType: prev === 'unlisted' ? 'all' : 'unlisted' })
  }
)

const legendaryOnlyAtom = atomWithListIncludes('rarities', 'Legendary')

const magicOnlyAtom = atomWithListIncludes('rarities', 'Magic')

const primeOnlyAtom = atomWithListIncludes('rarities', 'Prime')

const cyborgOnlyAtom = atomWithListIncludes('species', 'Cyborg')

const aiSpectreOnlyAtom = atomWithListIncludes('species', 'AISpectre')

const pandroidOnlyAtom = atomWithListIncludes('species', 'Pandroid')

const xgeneOnlyAtom = atomWithListIncludes('species', 'XGene')

const hardwareDruidOnlyAtom = atomWithListIncludes('careers', 'HardwareDruid')

const tradeNegotiatorOnlyAtom = atomWithListIncludes('careers', 'TradeNegotiator')

const hackerWizardOnlyAtom = atomWithListIncludes('careers', 'HackerWizard')

const roboWarriorOnlyAtom = atomWithListIncludes('careers', 'RoboWarrior')

const web3MonkOnlyAtom = atomWithListIncludes('careers', 'Web3Monk')

const searchQueryAtom = atom(
  get => get(listQueryAtom).searchTerms || '',
  (get, set, searchTerms: string) => {
    const args = get(listQueryAtom)
    set(listQueryAtom, { ...args, searchTerms })
  }
)


//
// Components
//

function FilterGroup({ title, children }: { title: string, children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(true)
  return (
    <div tw='flex flex-col px-5 py-5'>
      <header onClick={() => setIsOpen(i => !i)} tw='flex flex-row justify-between items-center cursor-pointer'>
        <h3 tw='text-base font-semibold'>{title}</h3>
        <motion.div
          animate={isOpen ? { rotate: 0 } : { rotate: 180 }}
          transition={{ duration: 0.15 }}
        >
          <FiChevronDown tw='h-6 w-6' />
        </motion.div>
      </header>
      <motion.div
        animate={isOpen ? { height: "auto", marginTop: "1rem" } : { height: 0, marginTop: 0 }}
        transition={{ duration: 0.15 }}
        tw='overflow-hidden flex flex-col gap-2'
      >
      {children}
      </motion.div>
    </div>
  );
}

interface FilterOptionProps {
  disabled?: boolean
  checked?: boolean
  readonly?: boolean
  valueAtom?: WritableAtom<boolean, any>
  children: ReactNode
}

function FilterOption({ disabled, checked, readonly, children, valueAtom }: FilterOptionProps) {
  const stubAtom = useMemo(() => {
    const _stubAtom: WritableAtom<boolean, any> = atom(false, (get, set, _value) => set(_stubAtom, !get(_stubAtom)))
    return _stubAtom
  }, [])
  const [isChecked, setIsChecked] = useAtom(valueAtom || stubAtom)
  const finalIsChecked = (checked !== undefined) ? checked : isChecked
  return (
    <div tw='flex flex-row justify-between items-center pr-1'>
      <Checkbox disabled={disabled} isChecked={finalIsChecked} isReadOnly={readonly} onChange={setIsChecked}>{children}</Checkbox>
    </div>
  )
}

const AsideFilterContainer = styled(motion.div)`
  ${tw`fixed left-0 top-52 h-full w-[300px] h-auto max-h-[60vh] overflow-y-scroll z-40 bg-[#18181b]`}

  &::-webkit-scrollbar {
    width: 8px;
  }

  > div {
    ${tw`border-b border-solid border-[#3F3F46]`}

    &:last-child {
      ${tw`border-b-0`}
    }
  }
`

function AsideFilter() {;
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <AsideFilterContainer
        animate={isOpen ? { x: 0 } : { x: -300 }}
        transition={{ duration: 0.15 }}
      >
        <FilterGroup title='Collection Type'>
          <FilterOption disabled>All</FilterOption>
          <FilterOption checked>Shell</FilterOption>
          <FilterOption disabled>Stake Pool V2</FilterOption>
        </FilterGroup>
        <FilterGroup title="Sale Status">
          <FilterOption valueAtom={forSaleAtom}>For Sale</FilterOption>
          <FilterOption valueAtom={notForSaleAtom}>Not for Sale</FilterOption>
        </FilterGroup>
        <FilterGroup title="Rarity">
          <FilterOption valueAtom={legendaryOnlyAtom}>Legendary</FilterOption>
          <FilterOption valueAtom={magicOnlyAtom}>Magic</FilterOption>
          <FilterOption valueAtom={primeOnlyAtom}>Prime Edition</FilterOption>
        </FilterGroup>
        <FilterGroup title="Species">
          <FilterOption valueAtom={cyborgOnlyAtom}>
            <span tw='flex'>
              <SpeciesIcon race='Cyborg' tw='w-6 h-6 mr-1' />
              Cyborg
            </span>
          </FilterOption>
          <FilterOption valueAtom={aiSpectreOnlyAtom}>
            <span tw='flex'>
              <SpeciesIcon race='AISpectre' tw='w-6 h-6 mr-1' />
              AI Spectre
            </span>
          </FilterOption>
          <FilterOption valueAtom={xgeneOnlyAtom}>
            <span tw='flex'>
              <SpeciesIcon race='X-Gene' tw='w-6 h-6 mr-1' />
              X-Gene
            </span>
          </FilterOption>
          <FilterOption valueAtom={pandroidOnlyAtom}>
            <span tw='flex'>
              <SpeciesIcon race='Pandroid' tw='w-6 h-6 mr-1' />
              Pandroid
            </span>
          </FilterOption>
        </FilterGroup>
        <FilterGroup title="Career">
          <FilterOption valueAtom={hackerWizardOnlyAtom}>
            <span tw='flex'>
              <CareerIcon career='HackerWizard' tw='w-6 h-6 mr-1' />
              Hacker Wizard
            </span>
          </FilterOption>
          <FilterOption valueAtom={hardwareDruidOnlyAtom}>
            <span tw='flex'>
              <CareerIcon career='HardwareDruid' tw='w-6 h-6 mr-1' />
              Hardware Druid
            </span>
          </FilterOption>
          <FilterOption valueAtom={roboWarriorOnlyAtom}>
            <span tw='flex'>
              <CareerIcon career='RoboWarrior' tw='w-6 h-6 mr-1' />
              Robo Warrior
            </span>
          </FilterOption>
          <FilterOption valueAtom={tradeNegotiatorOnlyAtom}>
            <span tw='flex'>
              <CareerIcon career='TradeNegotiator' tw='w-6 h-6 mr-1' />
              Trade Negotiator
            </span>
          </FilterOption>
          <FilterOption valueAtom={web3MonkOnlyAtom}>
            <span tw='flex'>
              <CareerIcon career='Web3Monk' tw='w-6 h-6 mr-1' />
              Web3 Monk
            </span>
          </FilterOption>
        </FilterGroup>
      </AsideFilterContainer>
      <motion.div
        tw='fixed left-56 top-[17.5rem] h-12 bg-[#18181b] text-white flex justify-center items-center z-40 rotate-90'
        animate={isOpen ? { x: 0, rotate: 90 } : { x: -300, rotate: 90 }}
        transition={{ duration: 0.15 }}
      >
        <Button width='12rem' onClick={() => setIsOpen(i => !i)}>
          <div tw='flex flex-row justify-between items-center w-full'>
            <span tw='uppercase font-secondary text-xl'>Filter</span>
            <span css={[tw`transition-all`, isOpen ? '' : tw`rotate-180`]}>
              <FiChevronDown tw='h-6 w-6' />
            </span>
          </div>
        </Button>
      </motion.div>
    </>
  )
}

function NftList() {
  const { ref, inView } = useInView()
  const listQuery = useAtomValue(listQueryAtom)
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    trpcQuery.nfts.list.getQueryKey(listQuery),
    async ({ pageParam = 1 }) => {
      const offset = ITEMS_PER_PAGE * (pageParam - 1)
      const page = await trpcVanilla.nfts.list.query({ ...listQuery, offset, limit: ITEMS_PER_PAGE })
      return page
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.items.length < ITEMS_PER_PAGE) {
          return undefined
        }
        return allPages.length + 1
      },
    },
  )

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [inView])

  const nfts = useMemo(() => R.map(transformUINft, R.unnest(R.map(i => i.items, data?.pages || []))), [data])

  return (
    <>
      <div tw='grid grid-cols-10 gap-5'>
        {nfts.map((nft, idx) => (
          <div tw='col-span-2' key={idx}>
            <LobbyCard nft={nft} />
          </div>
        ))}
        {nfts.length === 0 ? (
          <div tw="col-span-10 flex flex-col items-center justify-center py-10 gap-2">
            <h2 tw='text-[#71717A] text-2xl'>OOOOOOOOps...</h2>
            <p tw='text-[#71717A]'>Nothing matched.</p>
          </div>
        ) : null}
      </div>
      <div ref={ref} tw='w-full my-6 flex flex-row items-center justify-center'>
        {isFetchingNextPage ? (
          <Spinner color="brand.500" />
        ) : null}
      </div>
    </>
  )
}

function MarketplaceStats() {
  const stats = trpcQuery.marketplace.overview.useQuery()
  return (
    <div css={[tw`flex flex-row w-full bg-[rgba(22, 27, 31, 0.8)] p-10`, 'backdrop-filter: blur(10px);']}>
      <div tw="flex flex-grow flex-col justify-center items-center">
        <h3 tw="text-3xl font-semibold">{stats.data?.totalItems}</h3>
        <p tw="text-brand">Items</p>
      </div>
      <div tw="flex flex-grow flex-col justify-center items-center">
        <h3 tw="text-3xl font-semibold">{stats.data?.totalOwners}</h3>
        <p tw="text-brand">Owners</p>
      </div>
      <div tw="flex flex-grow flex-col justify-center items-center">
        <h3 tw="text-3xl font-semibold">{formatCurrency(stats.data?.floorPrice || 0)} PHA</h3>
        <p tw="text-brand">Floor Price</p>
      </div>
      <div tw="flex flex-grow flex-col justify-center items-center">
        <h3 tw="text-3xl font-semibold">{formatCurrency((stats.data?.totalVolumes || 0) / 1000)}K PHA</h3>
        <p tw="text-brand">Total Volume</p>
      </div>
    </div>
  )
}

function NftSearch() {
  const [query, setQuery] = useState('')
  const setSearch = useSetAtom(searchQueryAtom)
  const router = useRouter()
  useEffect(() => {
    setSearch('')
  }, [setSearch, router.pathname])
  return (
    <form
      onSubmit={(evt) => {
        evt.preventDefault()
        setSearch(query)
      }}
    >
      <FormControl>
        <InputGroup>
          <InputLeftElement pointerEvents='none'>
            <IoMdSearch color='gray.300' />
          </InputLeftElement>
          <Input
            borderWidth={0.5}
            background="#18181B"
            placeholder="Search by NFT number"
            onChange={ev => setQuery(ev.currentTarget.value)}
            type="search"
          />
        </InputGroup>
      </FormControl>
    </form>
  )
}

export default function MarketplacePage() {
  return (
    <>
      <Head>
        <title>Marketplace | PhalaWorld</title>
      </Head>
      <ConditionallyRender client>
        <div tw='grid grid-cols-12 gap-5 pt-24 max-w-10xl px-5 xl:mx-0 3xl:mx-auto'>
          <div tw="col-start-2 col-span-10">
            <Suspense>
              <IncubationUserBar />
            </Suspense>
          </div>
          <div tw="col-start-2 col-span-10 flex flex-col gap-8">
            <header tw='flex flex-row items-center justify-between mt-4'>
              <div>
                <Heading as='h1'>Marketplace</Heading>
                <p>Join the NFT revolution and build your collection</p>
              </div>
              <Link href="/activities">
                <Button variant="specialB" mr="6">
                  <ActivityListIcon tw="h-4 w-4 mr-3" />
                  ALL ACTIVITIES
                </Button>
              </Link>
            </header>
            <Suspense fallback={null}>
              <MarketplaceStats />
            </Suspense>
            <NftSearch />
          </div>
        </div>

        <div tw='w-full bg-black py-10'>
          <div tw='grid grid-cols-12 max-w-10xl px-5 xl:mx-0 3xl:mx-auto'>
            <div tw="col-start-2 col-span-10">
              <Suspense>
                <NftList />
              </Suspense>
            </div>
          </div>
        </div>

        <AsideFilter />
      </ConditionallyRender>
    </>
  );
}

MarketplacePage.getLayout = (page: ReactElement) => {
  return <AppLayout>{page}</AppLayout>;
};
